import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot, 
  getDocs,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db } from './firebase';
import { User, Post, Comment, XPAction, XP_REWARDS, LEVELS, Token, NFT, Message, Conversation } from '../types';

// Export des types pour les autres modules
export type { User, Post, Comment, Message, Conversation, Token, NFT };

// Interface pour UserProfile (alias de User)
export type UserProfile = User;

// Services utilisateurs
export const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const userDoc = await addDoc(collection(db, 'users'), {
      ...userData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return userDoc.id;
  } catch (error) {
    console.error('Erreur création utilisateur:', error);
    throw error;
  }
};

export const getUserByWallet = async (walletAddress: string): Promise<User | null> => {
  try {
    const q = query(collection(db, 'users'), where('walletAddress', '==', walletAddress));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) return null;
    
    const userDoc = querySnapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() } as User;
  } catch (error) {
    console.error('Erreur récupération utilisateur:', error);
    return null;
  }
};

export const getUserById = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) return null;
    
    return { id: userDoc.id, ...userDoc.data() } as User;
  } catch (error) {
    console.error('Erreur récupération utilisateur par ID:', error);
    return null;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<User>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Erreur mise à jour profil:', error);
    throw error;
  }
};

// Services posts
// Incrémente le compteur de vues d'un post
export const updatePostViews = async (postId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'posts', postId), {
      views: increment(1),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Erreur lors de l\'incrémentation des vues:', error);
  }
};
export const createPost = async (postData: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const postDoc = await addDoc(collection(db, 'posts'), {
      ...postData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Ajouter XP à l'utilisateur seulement si l'ID est défini
    if (postData.authorId) {
      try {
        await addXP(postData.authorId, 'post', postDoc.id);
      } catch (xpError) {
        console.warn('Erreur ajout XP (non critique):', xpError);
      }
    }
    
    return postDoc.id;
  } catch (error) {
    console.error('Erreur création post:', error);
    throw error;
  }
};

export const getPosts = async (limitCount: number = 20): Promise<Post[]> => {
  try {
    const q = query(
      collection(db, 'posts'), 
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'), 
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Post[];
  } catch (error) {
    console.error('Erreur récupération posts:', error);
    return [];
  }
};

export const deletePost = async (postId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'posts', postId));
  } catch (error) {
    console.error('Erreur suppression post:', error);
    throw error;
  }
};

export const sharePost = async (postId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'posts', postId), {
      shares: increment(1),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Erreur lors du partage:', error);
    throw error;
  }
};

export const likePost = async (postId: string, userId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'posts', postId), {
      likes: arrayUnion(userId),
      updatedAt: Timestamp.now()
    });
    
    if (userId) {
      try {
        await addXP(userId, 'like', postId);
      } catch (xpError) {
        console.warn('Erreur ajout XP (non critique):', xpError);
      }
    }
  } catch (error) {
    console.error('Erreur like post:', error);
    throw error;
  }
};

export const unlikePost = async (postId: string, userId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'posts', postId), {
      likes: arrayRemove(userId),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Erreur unlike post:', error);
    throw error;
  }
};

// Services commentaires
export const getCommentsByPostId = async (postId: string): Promise<Comment[]> => {
  try {
    const commentsQuery = query(
      collection(db, 'comments'),
      where('postId', '==', postId),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc')
    );
    
    const commentsSnapshot = await getDocs(commentsQuery);
    return commentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Comment[];
  } catch (error) {
    console.error('Erreur lors de la récupération des commentaires:', error);
    return [];
  }
};

export const createComment = async (commentData: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const commentDoc = await addDoc(collection(db, 'comments'), {
      ...commentData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    await updateDoc(doc(db, 'posts', commentData.postId), {
      comments: arrayUnion(commentDoc.id),
      updatedAt: Timestamp.now()
    });
    
    if (commentData.authorId) {
      try {
        await addXP(commentData.authorId, 'comment', commentData.postId);
      } catch (xpError) {
        console.warn('Erreur ajout XP (non critique):', xpError);
      }
    }
    
    return commentDoc.id;
  } catch (error) {
    console.error('Erreur création commentaire:', error);
    throw error;
  }
};

export const getPostById = async (postId: string): Promise<Post | null> => {
  try {
    const postDoc = await getDoc(doc(db, 'posts', postId));
    
    if (!postDoc.exists()) {
      return null;
    }
    
    const postData = postDoc.data();
    
    const commentsQuery = query(
      collection(db, 'comments'),
      where('postId', '==', postId),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc')
    );
    
    const commentsSnapshot = await getDocs(commentsQuery);
    const comments = commentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Comment[];
    
    return {
      id: postDoc.id,
      ...postData,
      comments
    } as Post;
  } catch (error) {
    console.error('Erreur lors de la récupération du post:', error);
    return null;
  }
};

// Système XP
export const addXP = async (userId: string, action: keyof typeof XP_REWARDS, relatedContentId?: string): Promise<void> => {
  try {
    if (!userId || typeof userId !== 'string') {
      console.warn('userId invalide pour addXP:', userId);
      return;
    }
    
    const xpGained = XP_REWARDS[action];
    console.log(`XP ajouté: ${xpGained} points pour l'action ${action}`);
  } catch (error) {
    console.error('Erreur lors de l\'ajout d\'XP:', error);
  }
};

// Fonction pour obtenir des tokens
export const getUserTokens = async (userId: string): Promise<Token[]> => {
  await new Promise(resolve => setTimeout(resolve, 800));

  return [
    {
      id: 'ethereum',
      name: 'Ethereum',
      symbol: 'ETH',
      amount: 3.45,
      value: 6843.78,
      change24h: 2.4,
      logoUrl: 'https://cryptologos.cc/logos/ethereum-eth-logo.png'
    },
    {
      id: 'solana',
      name: 'Solana',
      symbol: 'SOL',
      amount: 127.8,
      value: 8945.23,
      change24h: -5.7,
      logoUrl: 'https://cryptologos.cc/logos/solana-sol-logo.png'
    },
    {
      id: 'bitcoin',
      name: 'Bitcoin',
      symbol: 'BTC',
      amount: 0.125,
      value: 5843.75,
      change24h: 1.2,
      logoUrl: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png'
    }
  ];
};

// Service Firebase pour la gestion des messages et conversations
class FirebaseMessagesService {
  async createConversation(participants: string[]): Promise<string> {
    try {
      const conversationRef = await addDoc(collection(db, 'conversations'), {
        participants,
        createdAt: serverTimestamp(),
        lastMessage: null,
        lastMessageTimestamp: null,
        unreadCounts: Object.fromEntries(participants.map(p => [p, 0]))
      });
      return conversationRef.id;
    } catch (error) {
      console.error('Erreur création conversation:', error);
      throw error;
    }
  }

  async sendMessage(conversationId: string, senderId: string, content: string, type: 'text' | 'image' | 'gif' | 'nft' | 'token' = 'text', metadata?: any): Promise<string> {
    try {
      // Vérifier si la conversation existe
      const convDocRef = doc(db, 'conversations', conversationId);
      const convDocSnap = await getDoc(convDocRef);
      if (!convDocSnap.exists()) {
        // Créer la conversation si elle n'existe pas
        const participants = conversationId.split('_');
        await setDoc(convDocRef, {
          participants,
          createdAt: serverTimestamp(),
          lastMessage: content,
          lastMessageTimestamp: serverTimestamp(),
          unreadCounts: Object.fromEntries(participants.map(p => [p, 0]))
        });
      } else {
        // Mettre à jour la conversation existante
        await updateDoc(convDocRef, {
          lastMessage: content,
          lastMessageTimestamp: serverTimestamp()
        });
      }

      // Ajouter le message
      const messageRef = await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
        senderId,
        content,
        type,
        timestamp: serverTimestamp(),
        isRead: false
      });

      return messageRef.id;
    } catch (error) {
      console.error('Erreur envoi message:', error);
      throw error;
    }
  }

  getUserConversations(userId: string, onUpdate: (conversations: any[]) => void): () => void {
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageTimestamp', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const conversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      conversations.sort((a: any, b: any) => {
        if (!a.lastMessageTimestamp && !b.lastMessageTimestamp) return 0;
        if (!a.lastMessageTimestamp) return 1;
        if (!b.lastMessageTimestamp) return -1;
        
        const aTime = a.lastMessageTimestamp?.toDate?.() || new Date(0);
        const bTime = b.lastMessageTimestamp?.toDate?.() || new Date(0);
        return bTime.getTime() - aTime.getTime();
      });

      onUpdate(conversations);
    });
  }

  getConversationMessages(conversationId: string, onUpdate: (messages: any[]) => void): () => void {
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(50)
    );

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      onUpdate(messages);
    });
  }

  async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      const messagesQuery = query(
        collection(db, 'conversations', conversationId, 'messages'),
        where('senderId', '!=', userId),
        where('isRead', '==', false)
      );

      const snapshot = await getDocs(messagesQuery);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, { isRead: true });
      });

      batch.update(doc(db, 'conversations', conversationId), {
        [`unreadCounts.${userId}`]: 0
      });

      await batch.commit();
    } catch (error) {
      console.error('Erreur marquage messages lus:', error);
      throw error;
    }
  }

  async searchUsers(searchQuery: string, currentUserId: string): Promise<User[]> {
    if (!searchQuery.trim()) return [];

    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('displayName', '>=', searchQuery),
        where('displayName', '<=', searchQuery + '\uf8ff'),
        limit(10)
      );

      const snapshot = await getDocs(usersQuery);
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as User))
        .filter(user => user.id !== currentUserId);
    } catch (error) {
      console.error('Erreur recherche utilisateurs:', error);
      return [];
    }
  }

  // Méthodes manquantes pour useMessages
  getConversations(userId: string, onUpdate: (conversations: any[]) => void): () => void {
    return this.getUserConversations(userId, onUpdate);
  }

  getMessages(userId: string, otherParticipant: string, onUpdate: (messages: any[]) => void): () => void {
    // Créer ou récupérer la conversation entre les deux utilisateurs
    const conversationId = [userId, otherParticipant].sort().join('_');
    return this.getConversationMessages(conversationId, onUpdate);
  }

  getUsersOnlineStatus(userIds: string[], onUpdate: (statuses: { [userId: string]: boolean }) => void): () => void {
    // Simulation d'un statut en ligne
    const statuses = Object.fromEntries(userIds.map(id => [id, Math.random() > 0.5]));
    onUpdate(statuses);
    return () => {}; // Fonction de désabonnement vide
  }

  async deleteMessage(messageId: string): Promise<void> {
    // Simulation de suppression de message
    console.log(`Message ${messageId} supprimé`);
  }

  async deleteConversation(conversationId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'conversations', conversationId));
    } catch (error) {
      console.error('Erreur suppression conversation:', error);
      throw error;
    }
  }

  async getMessageStats(userId: string): Promise<any> {
    // Simulation de statistiques de messages
    return {
      totalMessages: Math.floor(Math.random() * 1000),
      totalConversations: Math.floor(Math.random() * 50),
      unreadMessages: Math.floor(Math.random() * 10)
    };
  }
}

class FirebaseCommentsService {
  async addComment(postId: string, userId: string, content: string): Promise<string> {
    try {
      const commentRef = await addDoc(collection(db, 'posts', postId, 'comments'), {
        userId,
        content,
        likes: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'posts', postId), {
        commentsCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return commentRef.id;
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      throw error;
    }
  }

  getPostComments(postId: string, onUpdate: (comments: Comment[]) => void): () => void {
    const q = query(
      collection(db, 'posts', postId, 'comments'),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    return onSnapshot(q, (snapshot) => {
      const comments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Comment[];
      onUpdate(comments);
    });
  }

  async deleteComment(postId: string, commentId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
      
      await updateDoc(doc(db, 'posts', postId), {
        commentsCount: increment(-1),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur suppression commentaire:', error);
      throw error;
    }
  }

  async likeComment(postId: string, commentId: string, userId: string): Promise<void> {
    try {
      // Utiliser la collection principale comments, pas les sous-collections
      await updateDoc(doc(db, 'comments', commentId), {
        likes: arrayUnion(userId),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur like commentaire:', error);
      throw error;
    }
  }

  async unlikeComment(postId: string, commentId: string, userId: string): Promise<void> {
    try {
      // Utiliser la collection principale comments, pas les sous-collections
      await updateDoc(doc(db, 'comments', commentId), {
        likes: arrayRemove(userId),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur unlike commentaire:', error);
      throw error;
    }
  }
}

// Créer les instances
const messagesService = new FirebaseMessagesService();
const commentsService = new FirebaseCommentsService();

// Exports pour les fonctions de commentaires
export const likeComment = async (postId: string, commentId: string, userId: string): Promise<void> => {
  return commentsService.likeComment(postId, commentId, userId);
};

export const unlikeComment = async (postId: string, commentId: string, userId: string): Promise<void> => {
  return commentsService.unlikeComment(postId, commentId, userId);
};

export { 
  messagesService,
  commentsService,
  FirebaseMessagesService,
  FirebaseCommentsService
};

export default {
  messages: messagesService,
  comments: commentsService
};

// Écouter les changements d'un post en temps réel
export const subscribeToPost = (postId: string, callback: (post: Post | null) => void) => {
  const postRef = doc(db, 'posts', postId);
  
  return onSnapshot(postRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      // Convertir les Timestamps en Date
      const post: Post = {
        ...data as Omit<Post, 'id' | 'createdAt' | 'updatedAt'>,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        // Convertir les timestamps du sondage si présent
        poll: data.poll ? {
          ...data.poll,
          createdAt: data.poll.createdAt?.toDate() || new Date(),
          expiresAt: data.poll.expiresAt?.toDate() || new Date()
        } : undefined
      };
      callback(post);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Erreur lors de l\'écoute du post:', error);
    callback(null);
  });
};

// Services de sondage
export const votePoll = async (postId: string, optionId: string, userId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post non trouvé');
    }
    
    const postData = postDoc.data() as Post;
    if (!postData.poll) {
      throw new Error('Aucun sondage trouvé dans ce post');
    }
    
    // Vérifier si le sondage n'est pas expiré
    const expiresAt = postData.poll.expiresAt instanceof Timestamp 
      ? postData.poll.expiresAt.toDate() 
      : new Date(postData.poll.expiresAt);
    if (expiresAt < new Date()) {
      throw new Error('Ce sondage a expiré');
    }
    
    // Vérifier si l'utilisateur a déjà voté
    if (postData.poll.voters.includes(userId)) {
      throw new Error('Vous avez déjà voté dans ce sondage');
    }
    
    // Vérifier si l'option existe
    const optionIndex = postData.poll.options.findIndex(opt => opt.id === optionId);
    if (optionIndex === -1) {
      throw new Error('Option non trouvée');
    }
    
    // Mise à jour avec batch pour assurer la cohérence
    const batch = writeBatch(db);
    
    // Ajouter le vote à l'option
    const updatedOptions = [...postData.poll.options];
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      votes: [...updatedOptions[optionIndex].votes, userId]
    };
    
    // Calculer le nouveau total de votes
    const totalVotes = updatedOptions.reduce((sum, option) => sum + option.votes.length, 0);
    
    // Mettre à jour le post
    batch.update(postRef, {
      [`poll.options`]: updatedOptions,
      [`poll.voters`]: arrayUnion(userId),
      [`poll.totalVotes`]: totalVotes,
      updatedAt: serverTimestamp()
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Erreur lors du vote:', error);
    throw error;
  }
};

export const voteMultiplePoll = async (postId: string, optionIds: string[], userId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post non trouvé');
    }
    
    const postData = postDoc.data() as Post;
    if (!postData.poll) {
      throw new Error('Aucun sondage trouvé dans ce post');
    }
    
    // Vérifier si le sondage autorise les choix multiples
    if (!postData.poll.allowMultipleChoice) {
      throw new Error('Ce sondage ne permet qu\'un seul choix');
    }
    
    // Vérifier si le sondage n'est pas expiré
    const expiresAt2 = postData.poll.expiresAt instanceof Timestamp 
      ? postData.poll.expiresAt.toDate() 
      : new Date(postData.poll.expiresAt);
    if (expiresAt2 < new Date()) {
      throw new Error('Ce sondage a expiré');
    }
    
    // Vérifier si l'utilisateur a déjà voté
    if (postData.poll.voters.includes(userId)) {
      throw new Error('Vous avez déjà voté dans ce sondage');
    }
    
    // Vérifier que toutes les options existent
    for (const optionId of optionIds) {
      if (!postData.poll.options.find(opt => opt.id === optionId)) {
        throw new Error(`Option ${optionId} non trouvée`);
      }
    }
    
    // Mise à jour avec batch pour assurer la cohérence
    const batch = writeBatch(db);
    
    // Ajouter les votes aux options sélectionnées
    const updatedOptions = postData.poll.options.map(option => ({
      ...option,
      votes: optionIds.includes(option.id) 
        ? [...option.votes, userId]
        : option.votes
    }));
    
    // Calculer le nouveau total de votes
    const totalVotes = updatedOptions.reduce((sum, option) => sum + option.votes.length, 0);
    
    // Mettre à jour le post
    batch.update(postRef, {
      [`poll.options`]: updatedOptions,
      [`poll.voters`]: arrayUnion(userId),
      [`poll.totalVotes`]: totalVotes,
      updatedAt: serverTimestamp()
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Erreur lors du vote multiple:', error);
    throw error;
  }
};
